import { Controller } from "@hotwired/stimulus"

// Coordinator actions on a round's stops (#8439): the ⋮ menus, and the merge
// dialog where the two stops' addresses are compared and one is kept.
//
// One dialog for the whole page, not one per stop: a round carries dozens of
// stops, and the dialog only ever shows one pair at a time. The eligible stops
// are handed over as a value — already filtered server-side (unsigned, same
// service, order stops), so this never offers something the service would
// refuse for a structural reason. The two rules it CAN predict, it explains:
// the auction house (which blocks) and the addresses (which is the choice).
export default class extends Controller {
  static targets = [
    "menu", "mergeDialog", "mergeForm", "mergeSelect", "mergeAlert", "mergeDetails",
    "mergeHouse", "mergeAddressBlock", "sourceAddress", "targetAddress", "mergeConfirm",
    "moveDialog", "moveForm", "moveSelect", "addDialog", "addForm",
    "assignDialog", "assignForm", "assignSelect",
    "ordersDialog", "ordersTitle", "ordersHint", "ordersIds", "ordersCopy", "ordersMessage"
  ]
  static values = { stops: Array, orders: Array, groups: Object }

  connect() {
    this.closeMenusOnOutsideClick = (event) => {
      if (event.target.closest("[data-round-stop-actions-target='menu']")) return
      if (event.target.closest("[data-action*='toggleMenu']")) return
      this.closeMenus()
    }
    document.addEventListener("click", this.closeMenusOnOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.closeMenusOnOutsideClick)
  }

  // Deleting goes through here, not through `data-turbo-method`: the app turns
  // Turbo Drive OFF (`Turbo.session.drive = false`), so that attribute is inert
  // and the browser followed the href as a plain GET — `/round_positions/188?
  // delete_type=stop`, which has no GET route, so the coordinator got a 404
  // (QA #8439). Same shape as `drag-and-drop#delete`, which is why the old card
  // worked.
  destroy(event) {
    event.preventDefault()
    event.stopPropagation()
    const link = event.currentTarget
    if (!window.confirm(link.dataset.confirmMessage)) return

    fetch(link.href, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken(),
        Accept: "text/vnd.turbo-stream.html, text/html, application/json"
      }
    })
      .then((response) => {
        // A refusal is silent otherwise: the guard answers 422 on a signed stop
        // or a merged head, and the page would simply reload unchanged.
        if (!response.ok) {
          window.alert(this.element.dataset.destroyFailedMessage)
          return
        }
        window.location.reload()
      })
      .catch(() => window.alert(this.element.dataset.destroyFailedMessage))
  }

  // The merge and move dialogs are submitted from here, not by the browser
  // (QA retest, bug 1). Their form is rendered with `url: '#'` and its action
  // rewritten once a stop is picked — but Rails binds a per-form CSRF token to
  // the action AT RENDER TIME, so the server refused every single submission
  // with "The change you wanted was rejected". The page's own token, sent as a
  // header, is valid whatever the path.
  //
  // The confirmation lives here too (bug 5): `turbo_confirm` does nothing with
  // Turbo Drive off, so an irreversible merge ran on one click. Same reason as
  // `destroy` above.
  submitDialog(event) {
    event.preventDefault()
    const form = event.currentTarget
    const message = form.dataset.confirmMessage
    if (message && !window.confirm(message)) return

    fetch(form.action, {
      method: "POST",
      headers: { "X-CSRF-Token": this.csrfToken(), Accept: "text/html" },
      body: new FormData(form),
      // `manual`, so the redirect is NOT followed here: following it renders
      // the page in the background and CONSUMES the flash, and the reload
      // below then showed the coordinator neither the success nor the rule
      // that refused them (review #8807).
      redirect: "manual"
    })
      // Both outcomes end the same way: the reload performs the redirect's GET
      // for real, and reads the flash the server left for it.
      .then(() => window.location.reload())
      .catch(() => window.location.reload())
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }

  // A counter of the overview opens what it counts (#8439), as ids to paste
  // elsewhere (#8952, prototype v1.13): one field, "id, id, id", and a button
  // that copies it whole. The title is the dialog's own ("Commandes
  // collectées"), not the counter's short label.
  openOrders(event) {
    event.preventDefault()
    const filter = event.currentTarget.dataset.filter
    const ids = this.ordersValue
      // An order can sit in two counters (#9319): "À traiter" and "Collecté".
      .filter((order) => filter === "fine_art" ? order.fine_art : order.statuses.includes(filter))
      .map((order) => order.id)

    this.ordersTitleTarget.textContent = event.currentTarget.dataset.title || ""
    this.ordersHintTarget.textContent =
      this.element.dataset.ordersHint.replace("%{count}", ids.length)
    this.ordersIdsTarget.value = ids.join(", ")
    this.ordersCopyTarget.disabled = ids.length === 0
    this.ordersMessageTarget.textContent = ids.length ? "" : this.element.dataset.ordersEmpty
    this.ordersDialogTarget.showModal()
  }

  selectOrderIds() {
    this.ordersIdsTarget.select()
  }

  // The Clipboard API first; it is missing outside a secure context (an http
  // staging, a page opened from a file), so the field is then selected and
  // `execCommand("copy")` tried — failing that, the selection is left for a
  // Ctrl+C, and the message says so.
  async copyOrderIds() {
    const value = this.ordersIdsTarget.value
    if (!value) return

    const count = value.split(", ").length
    const copied = await this.writeToClipboard(value)
    this.ordersMessageTarget.textContent = copied
      ? this.element.dataset.ordersCopied.replace("%{count}", count)
      : this.element.dataset.ordersCopyManual
  }

  async writeToClipboard(value) {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard API unavailable")
      await navigator.clipboard.writeText(value)
      return true
    } catch {
      this.ordersIdsTarget.focus()
      this.ordersIdsTarget.select()
      try {
        return document.execCommand("copy")
      } catch {
        return false
      }
    }
  }

  toggleMenu(event) {
    event.preventDefault()
    event.stopPropagation()
    const menu = event.currentTarget.nextElementSibling
    const wasOpen = !menu.hidden
    this.closeMenus()
    menu.hidden = wasOpen
  }

  closeMenus() {
    this.menuTargets.forEach((menu) => { menu.hidden = true })
  }

  // ----- merge ---------------------------------------------------------
  openMerge(event) {
    event.preventDefault()
    this.closeMenus()
    this.source = this.cardFor(event.currentTarget.dataset.stopId)
    if (!this.source) return

    const candidates = this.stopsValue.filter(
      (stop) => stop.id !== this.source.id && stop.service === this.source.service
    )
    if (candidates.length === 0) {
      window.alert(this.element.dataset.noCandidateMessage)
      return
    }

    this.mergeSelectTarget.innerHTML = candidates
      .map((stop) => `<option value="${stop.id}">${stop.day} — ${stop.name} — ${stop.address}</option>`)
      .join("")
    this.mergeFormTarget.action = this.mergeFormTarget.dataset.urlTemplate.replace(":id", this.source.id)
    this.previewMerge()
    this.mergeDialogTarget.showModal()
  }

  previewMerge() {
    const target = this.stopFor(this.mergeSelectTarget.value)
    if (!this.source || !target) return

    this.mergeHouseTarget.textContent =
      this.source.house === target.house ? this.source.house : `${this.source.house} / ${target.house}`

    const sameHouse = this.source.house === target.house && this.source.house !== ""
    // At a hub (#8674) one stop stands for every house behind the door, so two
    // houses may be paired when BOTH stops are there — the server's rule
    // (`Rounds::StopPairing#hub_pair?`, snapit-mobile#189), whichever is kept.
    const pairable = sameHouse || Boolean(this.source.hub && target.hub)
    this.mergeAlertTarget.hidden = pairable
    this.mergeConfirmTarget.disabled = !pairable
    this.mergeDetailsTarget.hidden = false

    const addressDiffers = this.source.address !== target.address
    this.mergeAddressBlockTarget.hidden = !pairable || !addressDiffers
    this.sourceAddressTarget.textContent = this.source.address
    this.targetAddressTarget.textContent = target.address
    if (addressDiffers) this.mergeFormTarget.querySelector("[value='source']").checked = true
  }

  // ----- move an order onto another stop --------------------------------
  // The tile names its OWN position, which is only the card's head for the
  // first order on it. `stopsValue` lists the CARDS the page draws, so every
  // other tile fell through the lookup and the menu did nothing at all (QA
  // retest, bug 2). The card is what the move must exclude from the
  // destinations; the position is what the move acts on.
  openMove(event) {
    event.preventDefault()
    this.closeMenus()
    const positionId = event.currentTarget.dataset.stopId
    const card = this.cardFor(positionId)

    const candidates = this.stopsValue.filter(
      (stop) => String(stop.id) !== String(card?.id) &&
        (!card || stop.service === card.service)
    )
    if (candidates.length === 0) {
      window.alert(this.element.dataset.noCandidateMessage)
      return
    }

    this.moveSelectTarget.innerHTML = candidates
      .map((stop) => `<option value="${stop.id}">${stop.day} — ${stop.name} — ${stop.address}</option>`)
      .join("")
    this.moveFormTarget.action = this.moveFormTarget.dataset.urlTemplate.replace(":id", positionId)
    this.moveDialogTarget.showModal()
  }

  // ----- put an unassigned order back onto a stop -----------------------
  // The order is known here and the stop is picked, the other way round from
  // `openAdd` — and unlike a move, the order sits on no stop, so every stop of
  // the round is a candidate.
  openAssign(event) {
    event.preventDefault()
    this.closeMenus()
    if (this.stopsValue.length === 0) {
      window.alert(this.element.dataset.noCandidateMessage)
      return
    }

    this.assignFormTarget.querySelector("[name='order_id']").value =
      event.currentTarget.dataset.orderId
    this.assignSelectTarget.innerHTML = this.stopsValue
      .map((stop) => `<option value="${stop.id}">${stop.day} — ${stop.name} — ${stop.address}</option>`)
      .join("")
    this.assignDialogTarget.showModal()
  }

  // ----- add an order to a stop -----------------------------------------
  openAdd(event) {
    event.preventDefault()
    this.closeMenus()
    this.addFormTarget.querySelector("[name='stop_id']").value = event.currentTarget.dataset.stopId
    this.addDialogTarget.showModal()
  }

  close(event) {
    event.preventDefault()
    event.target.closest("dialog").close()
  }

  stopFor(id) {
    return this.stopsValue.find((stop) => String(stop.id) === String(id))
  }

  // The card a position is drawn on — itself when it heads one.
  cardFor(positionId) {
    const headId = this.groupsValue[String(positionId)] ?? positionId
    return this.stopFor(headId)
  }
}
